import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, RefreshCw, AlertTriangle } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { Button } from '@/components/ui/Button';

// PR-1 — Afaqy tracker health. Every active vehicle, last 14 days of km
// coverage, flagged: ok / gaps / not reporting / unmapped. One-click backfill
// re-pulls the missing days so monthly km is complete.

interface DayKm { date: string; has: boolean; km: number }
interface Row {
  vehicleId: string; vehicleCode: string; plateEn: string | null; vehicleType: string;
  mapped: boolean; lastMovement: string | null; daysPresent: number; windowDays: number;
  status: 'ok' | 'gaps' | 'not_reporting' | 'unmapped'; missingDays: string[]; days: DayKm[];
}
interface Resp { summary: { total: number; ok: number; gaps: number; notReporting: number; unmapped: number }; items: Row[] }

const STATUS: Record<string, { label: string; cls: string }> = {
  ok:            { label: 'OK',            cls: 'border-emerald-500/40 text-emerald-600' },
  gaps:          { label: 'Gaps',          cls: 'border-amber-500/40 text-amber-600' },
  not_reporting: { label: 'Not reporting', cls: 'border-rose-500/40 text-rose-600' },
  unmapped:      { label: 'Unmapped',      cls: 'border-border text-muted-foreground' },
};

export default function TrackerHealthPage() {
  const qc = useQueryClient();
  const [scope, setScope] = useState('all');
  const { data, isLoading } = useQuery({
    queryKey: ['tracker-health', scope],
    queryFn: async () => (await apiClient.get<Resp>('/api/v1/tracker-health', { params: scope === 'all' ? {} : { projectScope: scope } })).data,
  });

  const backfill = useMutation({
    mutationFn: async (r: Row) => apiClient.post('/api/v1/tracker-health/backfill', { vehicleId: r.vehicleId, dates: r.missingDays }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tracker-health'] }),
  });

  const s = data?.summary;
  const rows = data?.items ?? [];

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="font-display text-lg">Tracker health (Afaqy km)</div>
          <div className="flex items-center gap-2">
            <select value={scope} onChange={(e) => setScope(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
              <option value="all">All projects</option><option value="neom">NEOM</option><option value="dumpers">Dumpers</option><option value="riyadh">Flatbed</option>
            </select>
            <Button variant="ghost" size="sm" onClick={() => { window.location.href = '/dashboard/settings'; }} className="gap-2"><ArrowLeft className="h-4 w-4" /> Settings</Button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-8">
        {s && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
            {[['Trucks', s.total, ''], ['OK', s.ok, 'text-emerald-600'], ['Gaps', s.gaps, 'text-amber-600'], ['Not reporting', s.notReporting, 'text-rose-600'], ['Unmapped', s.unmapped, 'text-muted-foreground']].map(([l, v, c], i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs text-muted-foreground">{l as string}</div>
                <div className={`text-2xl font-semibold mt-1 ${c as string}`}>{v as number}</div>
              </div>
            ))}
          </div>
        )}
        <div className="text-sm text-muted-foreground mb-3">Each cell is a day (oldest → yesterday). Red = no Afaqy km that day. Backfill re-pulls the missing days.</div>

        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-start font-medium px-3 py-2">Truck</th>
                <th className="text-start font-medium px-3 py-2">Status</th>
                <th className="text-start font-medium px-3 py-2">Last km</th>
                <th className="text-start font-medium px-3 py-2">Last 14 days</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>}
              {!isLoading && rows.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">No active vehicles.</td></tr>}
              {rows.map((r) => (
                <tr key={r.vehicleId} className="border-t border-border align-middle">
                  <td className="px-3 py-2"><div className="font-medium">{r.plateEn ?? r.vehicleCode}</div><div className="text-xs text-muted-foreground">{r.vehicleType}</div></td>
                  <td className="px-3 py-2"><span className={`text-xs rounded-full px-2 py-0.5 border ${STATUS[r.status].cls}`}>{STATUS[r.status].label}</span></td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs">{r.lastMovement ? r.lastMovement.slice(0, 10) : <span className="text-rose-500">never</span>}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-0.5">
                      {r.days.map((d) => (
                        <span key={d.date} title={`${d.date}: ${d.has ? Math.round(d.km) + ' km' : 'no data'}`}
                          className={`h-4 w-2.5 rounded-sm ${d.has ? (d.km > 1 ? 'bg-emerald-500' : 'bg-emerald-500/30') : 'bg-rose-500/70'}`} />
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-end whitespace-nowrap">
                    {r.status === 'unmapped' ? (
                      <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> map in Afaqy accounts</span>
                    ) : r.missingDays.length > 0 ? (
                      <Button size="sm" variant="outline" className="gap-1" disabled={backfill.isPending} onClick={() => backfill.mutate(r)}>
                        <RefreshCw className="h-3.5 w-3.5" /> Backfill {r.missingDays.length}d
                      </Button>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Backfill queues a re-pull; the tracker worker runs it within ~2 minutes. Refresh to see it fill in. Unmapped trucks need their plate linked in Settings → Afaqy accounts.</p>
      </main>
    </div>
  );
}
